// src/pages/Family.jsx - Family Allocation 

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { db } from '../firebase/config';
import {
    collection,
    query,
    orderBy,
    onSnapshot,
    addDoc,
    deleteDoc,
    doc,
    serverTimestamp,
    updateDoc,
    getDocs,
    where,
    writeBatch
} from 'firebase/firestore';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import Swal from 'sweetalert2';
import './Family.css';

const Family = () => {
    const { currentUser, userData, isAdmin } = useAuth();
    const { showToast } = useToast();

    // States
    const [loading, setLoading] = useState(true);
    const [members, setMembers] = useState([]);
    const [families, setFamilies] = useState([]);
    const [selectedFamily, setSelectedFamily] = useState(null);
    const [showFamilyModal, setShowFamilyModal] = useState(false);
    const [showMemberModal, setShowMemberModal] = useState(false);
    const [editingFamily, setEditingFamily] = useState(null);
    const [activeTab, setActiveTab] = useState('families');
    const [searchTerm, setSearchTerm] = useState('');
    const [userFamilies, setUserFamilies] = useState([]);

    // Form state
    const [familyFormData, setFamilyFormData] = useState({
        name: '',
        headId: '',
        headName: '',
        members: [],
        focus: '',
        startDate: new Date().toISOString().split('T')[0],
        endDate: ''
    });

    // Constants for PDF header
    const MINISTRY_NAME = "White Doves Ministry";
    const MINISTRY_ADDRESS = "P.O. Box 43844 - 00100, Nairobi, Kenya";
    const MINISTRY_PHONE = "+254 740086845";
    const MINISTRY_EMAIL = "info.whitedovesministry@gmail.com";
    const LOGO_PATH = "https://res.cloudinary.com/dblffn98e/image/upload/v1771566469/rbonf3jdrbuf5cu2qvbr.png";

    // Load data on mount
    useEffect(() => {
        fetchMembers();
        fetchFamilies();
    }, []);

    // Fetch current user's families whenever families change
    useEffect(() => {
        if (currentUser && families.length >= 0) {
            fetchUserFamilies();
        }
    }, [currentUser, families]);

    const fetchMembers = () => {
        const membersQuery = query(
            collection(db, 'users'),
            orderBy('fullName', 'asc')
        );

        const unsubscribe = onSnapshot(membersQuery, (snapshot) => {
            const membersData = [];
            snapshot.forEach(doc => {
                membersData.push({
                    id: doc.id,
                    ...doc.data()
                });
            });
            setMembers(membersData);
            setLoading(false);
        }, (error) => {
            console.error('Error fetching members:', error);
            showToast('Error loading members', 'error');
            setLoading(false);
        });

        return unsubscribe;
    };

    const fetchFamilies = () => {
        const familiesQuery = query(
            collection(db, 'families'),
            orderBy('createdAt', 'desc')
        );

        const unsubscribe = onSnapshot(familiesQuery, (snapshot) => {
            const familiesData = [];
            snapshot.forEach(doc => {
                familiesData.push({
                    id: doc.id,
                    ...doc.data()
                });
            });
            setFamilies(familiesData);
        }, (error) => {
            console.error('Error fetching families:', error);
        });

        return unsubscribe;
    };

    const fetchUserFamilies = () => {
        const userFamiliesData = families.filter(family =>
            family.members?.includes(currentUser.uid) ||
            family.headId === currentUser.uid
        );
        setUserFamilies(userFamiliesData);
    };

    // Get user name by ID
    const getUserName = (userId) => {
        const user = members.find(m => m.id === userId);
        return user?.fullName || user?.firstName || userId || 'Unknown';
    };

    // Get member by ID
    const getMemberById = (userId) => {
        return members.find(m => m.id === userId);
    };

    // Build ordered member list: head first, then other members
    const getOrderedFamilyMembers = (family) => {
        if (!family) return [];
        const memberIds = family.members || [];
        const headId = family.headId;
        
        // Head goes first (if head is in the members list)
        const ordered = [];
        if (headId && memberIds.includes(headId)) {
            const head = getMemberById(headId);
            if (head) ordered.push(head);
        } else if (headId) {
            // Head might not be in members array (edge case), still include
            const head = getMemberById(headId);
            if (head) ordered.push(head);
        }
        
        // Then the rest of the members (excluding head)
        memberIds.forEach(id => {
            if (id !== headId) {
                const member = getMemberById(id);
                if (member) ordered.push(member);
            }
        });
        
        return ordered;
    };

    // Total member count for a family (including head)
    const getFamilyMemberCount = (family) => {
        if (!family) return 0;
        const ids = new Set(family.members || []);
        if (family.headId) ids.add(family.headId);
        return ids.size;
    };

    // Mask phone number (for display)
    const maskPhoneNumber = (phone) => {
        if (!phone) return 'N/A';
        const cleaned = phone.replace(/\D/g, '');
        if (cleaned.length < 5) return phone;
        const firstThree = cleaned.substring(0, 3);
        const lastTwo = cleaned.substring(cleaned.length - 2);
        const maskedMiddle = '*'.repeat(Math.min(cleaned.length - 5, 5));
        return `${firstThree}${maskedMiddle}${lastTwo}`;
    };

    // Check if a member is already in a family
    const isMemberInAnyFamily = (memberId) => {
        return families.some(family => 
            family.members?.includes(memberId) || family.headId === memberId
        );
    };

    // Get total assigned members (including heads)
    const getAssignedMembersCount = () => {
        const assignedIds = new Set();
        families.forEach(family => {
            family.members?.forEach(memberId => assignedIds.add(memberId));
            if (family.headId) assignedIds.add(family.headId);
        });
        return assignedIds.size;
    };

    // Get unassigned members
    const getUnassignedMembers = () => {
        const assignedIds = new Set();
        families.forEach(family => {
            family.members?.forEach(memberId => assignedIds.add(memberId));
            if (family.headId) assignedIds.add(family.headId);
        });
        return members.filter(member => !assignedIds.has(member.id));
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return '';
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch {
            return 'Invalid date';
        }
    };

    // ====================
    // PDF GENERATION
    // ====================

    const addMinistryHeader = async (doc) => {
        const pageWidth = doc.internal.pageSize.getWidth();
        
        try {
            const logoResponse = await fetch(LOGO_PATH);
            const logoBlob = await logoResponse.blob();
            const logoDataUrl = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.readAsDataURL(logoBlob);
            });
            
            doc.addImage(logoDataUrl, 'PNG', 14, 5, 30, 30);
        } catch (error) {
            console.error('Error loading logo:', error);
        }
        
        doc.setFillColor(16, 52, 166);
        doc.rect(0, 0, pageWidth, 40, 'F');
        
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(20);
        doc.setFont('helvetica', 'bold');
        doc.text(MINISTRY_NAME, pageWidth / 2, 20, { align: 'center' });
        
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text('"Arise and Shine" - Isaiah 60:1', pageWidth / 2, 30, { align: 'center' });
        
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.text(`${MINISTRY_ADDRESS} | Tel: ${MINISTRY_PHONE} | Email: ${MINISTRY_EMAIL}`, pageWidth / 2, 48, { align: 'center' });
        
        doc.setDrawColor(16, 52, 166);
        doc.setLineWidth(0.5);
        doc.line(20, 52, pageWidth - 20, 52);
        
        return 55;
    };

    const addFooter = (doc) => {
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text(`White Doves Ministry - Family Allocation Report`, pageWidth / 2, pageHeight - 10, { align: 'center' });
        doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, pageHeight - 5, { align: 'center' });
    };

    // Export all families - Overview
    const exportFamiliesPDF = async () => {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        let yPos = await addMinistryHeader(doc);

        doc.setFontSize(16);
        doc.setTextColor(16, 52, 166);
        doc.setFont('helvetica', 'bold');
        doc.text('FAMILY ALLOCATION REPORT', pageWidth / 2, yPos + 10, { align: 'center' });
        
        yPos += 25;
        doc.setFontSize(11);
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'normal');
        doc.text(`Generated By: ${userData?.fullName || 'Admin'}`, 20, yPos);
        doc.text(`Date: ${new Date().toLocaleString()}`, 20, yPos + 8);
        doc.text(`Total Families: ${families.length}`, 20, yPos + 16);
        doc.text(`Assigned Members: ${getAssignedMembersCount()}`, 20, yPos + 24);
        doc.text(`Unassigned Members: ${getUnassignedMembers().length}`, 20, yPos + 32);

        const familiesData = families.map(family => [
            family.name,
            getFamilyMemberCount(family),
            family.headName || 'Not Assigned',
            family.focus || 'N/A'
        ]);

        doc.autoTable({
            startY: yPos + 40,
            head: [['Family Name', 'Members', 'Family Head', 'Focus Area']],
            body: familiesData,
            theme: 'striped',
            headStyles: { fillColor: [16, 52, 166], textColor: 255, fontSize: 10 },
            bodyStyles: { fontSize: 9 },
            margin: { left: 20, right: 20 },
            foot: [[
                'TOTAL',
                families.reduce((sum, f) => sum + getFamilyMemberCount(f), 0),
                '', ''
            ]],
            footStyles: { fillColor: [240, 240, 240], fontStyle: 'bold', fontSize: 9 }
        });

        addFooter(doc);
        doc.save(`family-allocation-report-${Date.now()}.pdf`);
        showToast('PDF exported successfully!', 'success');
    };

    // Export full report with all members and contacts
    const exportFullReportPDF = async () => {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        let yPos = await addMinistryHeader(doc);

        doc.setFontSize(16);
        doc.setTextColor(16, 52, 166);
        doc.setFont('helvetica', 'bold');
        doc.text('FULL FAMILY MEMBERS REPORT', pageWidth / 2, yPos + 10, { align: 'center' });
        
        yPos += 25;
        doc.setFontSize(11);
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'normal');
        doc.text(`Generated By: ${userData?.fullName || 'Admin'}`, 20, yPos);
        doc.text(`Date: ${new Date().toLocaleString()}`, 20, yPos + 8);

        const totalMembers = members.length;
        const assignedMembers = getAssignedMembersCount();
        const unassignedMembers = totalMembers - assignedMembers;

        const statsData = [
            ['Total Ministry Members', totalMembers],
            ['Assigned to Families', assignedMembers],
            ['Unassigned Members', unassignedMembers],
            ['Total Families', families.length]
        ];

        doc.autoTable({
            startY: yPos + 16,
            head: [['Metric', 'Value']],
            body: statsData,
            theme: 'striped',
            headStyles: { fillColor: [16, 52, 166], textColor: 255, fontSize: 10 },
            bodyStyles: { fontSize: 9 },
            margin: { left: 20, right: 20 }
        });

        let finalY = doc.lastAutoTable.finalY + 15;

        for (const family of families) {
            if (finalY > 240) {
                doc.addPage();
                finalY = 20;
            }

            doc.setFontSize(14);
            doc.setTextColor(16, 52, 166);
            doc.setFont('helvetica', 'bold');
            doc.text(`${family.name}`, 20, finalY);
            doc.setFontSize(10);
            doc.setTextColor(80, 80, 80);
            doc.setFont('helvetica', 'normal');
            doc.text(
                `Head: ${family.headName || 'Not Assigned'}  |  Focus: ${family.focus || 'N/A'}  |  Total Members: ${getFamilyMemberCount(family)}`, 
                20, 
                finalY + 6
            );
            doc.line(20, finalY + 10, pageWidth - 20, finalY + 10);

            // Build ordered rows: head first, then members
            const orderedMembers = getOrderedFamilyMembers(family);
            const memberRows = orderedMembers.map(member => [
                member.fullName || member.firstName || 'Unknown',
                member.email || 'N/A',
                member.phone || 'N/A',
                member.yearName || 'N/A',
                member.id === family.headId ? 'Head' : 'Member'
            ]);

            doc.autoTable({
                startY: finalY + 14,
                head: [['Name', 'Email', 'Phone', 'Year Group', 'Role']],
                body: memberRows,
                theme: 'striped',
                headStyles: { fillColor: [16, 52, 166], textColor: 255, fontSize: 9 },
                bodyStyles: { fontSize: 9 },
                margin: { left: 20, right: 20 },
                columnStyles: {
                    0: { cellWidth: 45 },
                    1: { cellWidth: 55 },
                    2: { cellWidth: 30 },
                    3: { cellWidth: 25 },
                    4: { cellWidth: 20 }
                }
            });

            finalY = doc.lastAutoTable.finalY + 15;
        }

        // Unassigned members
        const unassigned = getUnassignedMembers();
        if (unassigned.length > 0) {
            if (finalY > 220) {
                doc.addPage();
                finalY = 20;
            }

            doc.setFontSize(14);
            doc.setTextColor(200, 50, 50);
            doc.setFont('helvetica', 'bold');
            doc.text('Unassigned Members', 20, finalY);
            doc.line(20, finalY + 5, pageWidth - 20, finalY + 5);

            const unassignedRows = unassigned.map(member => [
                member.fullName || member.firstName || 'Unknown',
                member.email || 'N/A',
                member.phone || 'N/A',
                member.yearName || 'N/A'
            ]);

            doc.autoTable({
                startY: finalY + 10,
                head: [['Name', 'Email', 'Phone', 'Year Group']],
                body: unassignedRows,
                theme: 'striped',
                headStyles: { fillColor: [200, 50, 50], textColor: 255, fontSize: 9 },
                bodyStyles: { fontSize: 9 },
                margin: { left: 20, right: 20 },
                columnStyles: {
                    0: { cellWidth: 50 },
                    1: { cellWidth: 60 },
                    2: { cellWidth: 35 },
                    3: { cellWidth: 25 }
                }
            });
        }

        addFooter(doc);
        doc.save(`full-family-members-report-${Date.now()}.pdf`);
        showToast('Full report exported successfully!', 'success');
    };

    // Export single family report
    const exportFamilyReport = async (familyId) => {
        const family = families.find(f => f.id === familyId);
        if (!family) return;

        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        let yPos = await addMinistryHeader(doc);

        doc.setFontSize(16);
        doc.setTextColor(16, 52, 166);
        doc.setFont('helvetica', 'bold');
        doc.text('FAMILY MEMBERS LIST', pageWidth / 2, yPos + 10, { align: 'center' });
        
        yPos += 25;

        doc.setFontSize(14);
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'bold');
        doc.text(`${family.name}`, 20, yPos);

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(80, 80, 80);
        doc.text(`Family Head: ${family.headName || 'Not Assigned'}`, 20, yPos + 8);
        doc.text(`Focus Area: ${family.focus || 'N/A'}`, 20, yPos + 15);
        doc.text(`Start Date: ${formatDate(family.startDate) || 'N/A'}`, 20, yPos + 22);
        if (family.endDate) {
            doc.text(`End Date: ${formatDate(family.endDate)}`, 20, yPos + 29);
        }
        doc.text(
            `Total Members: ${getFamilyMemberCount(family)}`, 
            20, 
            yPos + (family.endDate ? 36 : 29)
        );
        doc.text(
            `Generated: ${new Date().toLocaleString()}`, 
            20, 
            yPos + (family.endDate ? 43 : 36)
        );

        const tableStartY = yPos + (family.endDate ? 51 : 44);

        // Ordered: head first
        const orderedMembers = getOrderedFamilyMembers(family);
        const memberRows = orderedMembers.map((member, index) => [
            index + 1,
            member.fullName || member.firstName || 'Unknown',
            member.email || 'N/A',
            member.phone || 'N/A',
            member.yearName || 'N/A',
            member.id === family.headId ? 'Head' : 'Member'
        ]);

        doc.autoTable({
            startY: tableStartY,
            head: [['#', 'Name', 'Email', 'Phone', 'Year Group', 'Role']],
            body: memberRows,
            theme: 'striped',
            headStyles: { fillColor: [16, 52, 166], textColor: 255, fontSize: 9 },
            bodyStyles: { fontSize: 9 },
            margin: { left: 20, right: 20 },
            columnStyles: {
                0: { cellWidth: 10, halign: 'center' },
                1: { cellWidth: 45 },
                2: { cellWidth: 55 },
                3: { cellWidth: 30 },
                4: { cellWidth: 25 },
                5: { cellWidth: 20 }
            }
        });

        addFooter(doc);
        const safeName = family.name.replace(/[^a-z0-9]/gi, '-').toLowerCase();
        doc.save(`family-${safeName}-members-${Date.now()}.pdf`);
        showToast('Family report exported successfully!', 'success');
    };

    // Handle family creation/update
    const handleFamilySubmit = async (e) => {
        e.preventDefault();

        if (!familyFormData.name.trim()) {
            showToast('Please enter a family name', 'error');
            return;
        }

        // Effective members = head + selected members (deduped)
        const effectiveMembers = Array.from(new Set([
            ...(familyFormData.headId ? [familyFormData.headId] : []),
            ...familyFormData.members
        ]));

        if (effectiveMembers.length === 0) {
            showToast('Please select at least one member', 'error');
            return;
        }

        if (effectiveMembers.length > 8) {
            showToast('Family cannot have more than 8 members (including the head)', 'error');
            return;
        }

        setLoading(true);

        try {
            const adminName = await getAdminName();
            const familyData = {
                name: familyFormData.name.trim(),
                headId: familyFormData.headId,
                headName: familyFormData.headId ? getUserName(familyFormData.headId) : '',
                members: effectiveMembers,
                memberNames: effectiveMembers.map(id => getUserName(id)),
                focus: familyFormData.focus || '',
                startDate: familyFormData.startDate,
                endDate: familyFormData.endDate || '',
                status: 'active',
                updatedBy: currentUser.uid,
                updatedByName: adminName,
                updatedAt: serverTimestamp()
            };

            if (editingFamily) {
                const familyRef = doc(db, 'families', editingFamily.id);
                await updateDoc(familyRef, familyData);
                showToast('Family updated successfully!', 'success');
            } else {
                familyData.createdAt = serverTimestamp();
                familyData.createdBy = currentUser.uid;
                familyData.createdByName = adminName;
                await addDoc(collection(db, 'families'), familyData);
                showToast('Family created successfully!', 'success');
            }

            setShowFamilyModal(false);
            resetFamilyForm();
        } catch (error) {
            console.error('Error saving family:', error);
            showToast('Error saving family', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Get admin name
    const getAdminName = async () => {
        try {
            const adminDoc = await getDocs(query(
                collection(db, 'admins'),
                where('email', '==', currentUser.email)
            ));
            if (!adminDoc.empty) {
                const adminData = adminDoc.docs[0].data();
                return adminData.fullName || adminData.name || currentUser.email;
            }
            return userData?.fullName || currentUser.email;
        } catch {
            return userData?.fullName || currentUser.email;
        }
    };

    // Reset form
    const resetFamilyForm = () => {
        setFamilyFormData({
            name: '',
            headId: '',
            headName: '',
            members: [],
            focus: '',
            startDate: new Date().toISOString().split('T')[0],
            endDate: ''
        });
        setEditingFamily(null);
    };

    // Handle edit family
    const handleEditFamily = (family) => {
        setEditingFamily(family);
        // Exclude head from members checkbox list (head is separate)
        const membersWithoutHead = (family.members || []).filter(id => id !== family.headId);
        setFamilyFormData({
            name: family.name || '',
            headId: family.headId || '',
            headName: family.headName || '',
            members: membersWithoutHead,
            focus: family.focus || '',
            startDate: family.startDate || new Date().toISOString().split('T')[0],
            endDate: family.endDate || ''
        });
        setShowFamilyModal(true);
    };

    // Handle delete family
    const handleDeleteFamily = async (familyId) => {
        const result = await Swal.fire({
            title: 'Delete Family?',
            text: 'This will remove the family group. Members will become unassigned. This action cannot be undone!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                await deleteDoc(doc(db, 'families', familyId));
                showToast('Family deleted successfully!', 'success');
            } catch (error) {
                console.error('Error deleting family:', error);
                showToast('Error deleting family', 'error');
            }
        }
    };

    // Get filtered families
    const filteredFamilies = families.filter(family =>
        family.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        family.headName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        family.memberNames?.some(name => name.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    // Render admin dashboard
    const renderAdminDashboard = () => (
        <div className="family-admin-dashboard">
            <div className="family-stats-grid">
                <div className="family-stat-card">
                    <div className="family-stat-icon"><i className="fas fa-users"></i></div>
                    <div className="family-stat-info">
                        <div className="family-stat-value">{members.length}</div>
                        <div className="family-stat-label">Total Members</div>
                    </div>
                </div>
                <div className="family-stat-card">
                    <div className="family-stat-icon"><i className="fas fa-user-check"></i></div>
                    <div className="family-stat-info">
                        <div className="family-stat-value">{getAssignedMembersCount()}</div>
                        <div className="family-stat-label">Assigned to Families</div>
                    </div>
                </div>
                <div className="family-stat-card">
                    <div className="family-stat-icon"><i className="fas fa-user-minus"></i></div>
                    <div className="family-stat-info">
                        <div className="family-stat-value">{getUnassignedMembers().length}</div>
                        <div className="family-stat-label">Unassigned Members</div>
                    </div>
                </div>
                <div className="family-stat-card">
                    <div className="family-stat-icon"><i className="fas fa-home"></i></div>
                    <div className="family-stat-info">
                        <div className="family-stat-value">{families.length}</div>
                        <div className="family-stat-label">Active Families</div>
                    </div>
                </div>
            </div>

            <div className="family-actions-bar">
                <button
                    className="family-btn-primary"
                    onClick={() => {
                        resetFamilyForm();
                        setShowFamilyModal(true);
                    }}
                >
                    <i className="fas fa-plus"></i> Create Family
                </button>
                <button
                    className="family-btn-secondary"
                    onClick={exportFamiliesPDF}
                >
                    <i className="fas fa-file-pdf"></i> Export Families
                </button>
                <button
                    className="family-btn-info"
                    onClick={exportFullReportPDF}
                >
                    <i className="fas fa-file-pdf"></i> Full Members Report
                </button>
            </div>

            <div className="family-families-section">
                <h2>Family Groups</h2>
                <div className="family-search-bar">
                    <input
                        type="text"
                        placeholder="Search families..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <i className="fas fa-search"></i>
                </div>

                <div className="family-families-grid">
                    {filteredFamilies.length === 0 ? (
                        <div className="family-empty-state">
                            <i className="fas fa-home"></i>
                            <p>No families found. Create your first family!</p>
                        </div>
                    ) : (
                        filteredFamilies.map(family => (
                            <div key={family.id} className="family-card">
                                <div className="family-card-header">
                                    <h3>{family.name}</h3>
                                    <span className={`family-status ${family.status}`}>
                                        {family.status || 'active'}
                                    </span>
                                </div>
                                <div className="family-card-body">
                                    <div className="family-info">
                                        <div className="family-info-item">
                                            <i className="fas fa-user-tie"></i>
                                            <span><strong>Head:</strong> {family.headName || 'Not Assigned'}</span>
                                        </div>
                                        <div className="family-info-item">
                                            <i className="fas fa-users"></i>
                                            <span>
                                                <strong>Members ({getFamilyMemberCount(family)}):</strong>{' '}
                                                {family.memberNames?.join(', ') || 'None'}
                                            </span>
                                        </div>
                                        {family.focus && (
                                            <div className="family-info-item">
                                                <i className="fas fa-bullseye"></i>
                                                <span><strong>Focus:</strong> {family.focus}</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="family-actions">
                                        <button
                                            className="family-btn-small family-btn-view"
                                            onClick={() => {
                                                setSelectedFamily(family);
                                                setShowMemberModal(true);
                                            }}
                                        >
                                            <i className="fas fa-eye"></i> View
                                        </button>
                                        <button
                                            className="family-btn-small family-btn-edit"
                                            onClick={() => handleEditFamily(family)}
                                        >
                                            <i className="fas fa-edit"></i> Edit
                                        </button>
                                        <button
                                            className="family-btn-small family-btn-export"
                                            onClick={() => exportFamilyReport(family.id)}
                                        >
                                            <i className="fas fa-file-pdf"></i> Members List
                                        </button>
                                        <button
                                            className="family-btn-small family-btn-delete"
                                            onClick={() => handleDeleteFamily(family.id)}
                                        >
                                            <i className="fas fa-trash"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );

    // Render user dashboard (member view)
    const renderUserDashboard = () => (
        <div className="family-user-dashboard">
            <div className="family-user-header">
                <h2>My Family</h2>
            </div>

            {/* Stats */}
            <div className="family-stats-grid">
                <div className="family-stat-card">
                    <div className="family-stat-icon"><i className="fas fa-home"></i></div>
                    <div className="family-stat-info">
                        <div className="family-stat-value">{userFamilies.length}</div>
                        <div className="family-stat-label">My Families</div>
                    </div>
                </div>
                {userFamilies.length > 0 && (
                    <>
                        <div className="family-stat-card">
                            <div className="family-stat-icon"><i className="fas fa-user-friends"></i></div>
                            <div className="family-stat-info">
                                <div className="family-stat-value">
                                    {userFamilies.reduce((sum, f) => sum + getFamilyMemberCount(f), 0)}
                                </div>
                                <div className="family-stat-label">Total Family Members</div>
                            </div>
                        </div>
                        <div className="family-stat-card">
                            <div className="family-stat-icon"><i className="fas fa-user-tie"></i></div>
                            <div className="family-stat-info">
                                <div className="family-stat-value">
                                    {userFamilies.filter(f => f.headId === currentUser.uid).length > 0 ? 'Yes' : 'No'}
                                </div>
                                <div className="family-stat-label">I'm a Family Head</div>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Families */}
            <div className="family-families-section">
                <h3>My Family Groups</h3>
                <div className="family-families-grid">
                    {userFamilies.length === 0 ? (
                        <div className="family-empty-state">
                            <i className="fas fa-home"></i>
                            <p>You are not assigned to any family yet.</p>
                            <p className="family-empty-sub">Contact your administrator to be added to a family.</p>
                        </div>
                    ) : (
                        userFamilies.map(family => {
                            const isHead = family.headId === currentUser.uid;
                            // All members except current user, head first
                            const orderedMembers = getOrderedFamilyMembers(family);
                            const otherMembers = orderedMembers.filter(m => m.id !== currentUser.uid);

                            return (
                                <div key={family.id} className="family-card family-card-user">
                                    <div className="family-card-header">
                                        <h3>{family.name}</h3>
                                        <span className={`family-status ${family.status}`}>
                                            {family.status || 'active'}
                                        </span>
                                    </div>
                                    <div className="family-card-body">
                                        <div className="family-info">
                                            <div className="family-info-item">
                                                <i className="fas fa-user-tie"></i>
                                                <span>
                                                    <strong>Family Head:</strong> {family.headName || 'Not Assigned'}
                                                    {isHead && <span className="family-you-badge">You</span>}
                                                </span>
                                            </div>
                                            {family.focus && (
                                                <div className="family-info-item">
                                                    <i className="fas fa-bullseye"></i>
                                                    <span><strong>Focus:</strong> {family.focus}</span>
                                                </div>
                                            )}
                                            <div className="family-info-item">
                                                <i className="fas fa-users"></i>
                                                <span>
                                                    <strong>Total Members:</strong> {getFamilyMemberCount(family)}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Family Members List */}
                                        <div className="family-members-preview">
                                            <h4><i className="fas fa-user-friends"></i> Family Members</h4>
                                            {otherMembers.length === 0 ? (
                                                <p className="family-no-members">You are the only member in this family.</p>
                                            ) : (
                                                <ul className="family-members-list">
                                                    {otherMembers.map(member => (
                                                        <li key={member.id}>
                                                            <i className="fas fa-user"></i>
                                                            <div className="family-member-info-block">
                                                                <span className="family-member-name">
                                                                    {member.fullName || member.firstName || 'Unknown'}
                                                                    {member.id === family.headId && (
                                                                        <span className="family-head-badge">Head</span>
                                                                    )}
                                                                </span>
                                                                <small className="family-member-contact">
                                                                    {member.phone && <span><i className="fas fa-phone"></i> {member.phone}</span>}
                                                                    {member.email && <span><i className="fas fa-envelope"></i> {member.email}</span>}
                                                                </small>
                                                            </div>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>

                                        <div className="family-actions">
                                            <button
                                                className="family-btn-small family-btn-export"
                                                onClick={() => exportFamilyReport(family.id)}
                                            >
                                                <i className="fas fa-file-pdf"></i> Members List
                                            </button>
                                            <button
                                                className="family-btn-small family-btn-view"
                                                onClick={() => {
                                                    setSelectedFamily(family);
                                                    setShowMemberModal(true);
                                                }}
                                            >
                                                <i className="fas fa-eye"></i> View Details
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );

    if (loading && members.length === 0) {
        return (
            <div className="family-loading">
                <i className="fas fa-spinner fa-spin"></i>
                <p>Loading Family Allocation...</p>
            </div>
        );
    }

    return (
        <div className="family-page">
            <header className="family-header">
                <div>
                    <h1>Family Allocation</h1>
                    <p className="family-subtitle">
                        {isAdmin 
                            ? 'Allocate members into families and view family rosters' 
                            : 'View your family group and fellow members'}
                    </p>
                </div>
            </header>

            {/* Tabs */}
            <div className="family-tabs">
                <button
                    className={`family-tab ${activeTab === 'families' ? 'active' : ''}`}
                    onClick={() => setActiveTab('families')}
                >
                    <i className="fas fa-home"></i> {isAdmin ? 'My Families' : 'My Family'}
                </button>
                {isAdmin && (
                    <button
                        className={`family-tab ${activeTab === 'admin' ? 'active' : ''}`}
                        onClick={() => setActiveTab('admin')}
                    >
                        <i className="fas fa-chart-bar"></i> Admin Dashboard
                    </button>
                )}
            </div>

            {/* Content */}
            <div className="family-content">
                {activeTab === 'families' && renderUserDashboard()}
                {activeTab === 'admin' && isAdmin && renderAdminDashboard()}
            </div>

            {/* Family Modal */}
            {showFamilyModal && (
                <div className="family-modal" onClick={() => setShowFamilyModal(false)}>
                    <div className="family-modal-content" onClick={e => e.stopPropagation()}>
                        <div className="family-modal-header">
                            <h3>{editingFamily ? 'Edit Family' : 'Create New Family'}</h3>
                            <button
                                className="family-modal-close"
                                onClick={() => setShowFamilyModal(false)}
                            >
                                &times;
                            </button>
                        </div>
                        <form onSubmit={handleFamilySubmit} className="family-form">
                            <div className="family-form-group">
                                <label>Family Name *</label>
                                <input
                                    type="text"
                                    className="family-form-control"
                                    value={familyFormData.name}
                                    onChange={(e) => setFamilyFormData({ ...familyFormData, name: e.target.value })}
                                    placeholder="e.g., Little Heaven"
                                    required
                                />
                            </div>

                            <div className="family-form-group">
                                <label>Family Head * (included in member count)</label>
                                <select
                                    className="family-form-control"
                                    value={familyFormData.headId}
                                    onChange={(e) => {
                                        const headId = e.target.value;
                                        // If the new head was in the members list, remove them (head is separate)
                                        setFamilyFormData({
                                            ...familyFormData,
                                            headId: headId,
                                            headName: headId ? getUserName(headId) : '',
                                            members: familyFormData.members.filter(id => id !== headId)
                                        });
                                    }}
                                >
                                    <option value="">Select Family Head</option>
                                    {members
                                        .filter(member => {
                                            if (editingFamily) {
                                                return !isMemberInAnyFamily(member.id) 
                                                    || editingFamily.members?.includes(member.id)
                                                    || editingFamily.headId === member.id;
                                            }
                                            return !isMemberInAnyFamily(member.id);
                                        })
                                        .map(member => (
                                            <option key={member.id} value={member.id}>
                                                {member.fullName || member.firstName || member.email}
                                            </option>
                                        ))}
                                </select>
                            </div>

                            <div className="family-form-group">
                                <label>Additional Members (Select up to 7)</label>
                                <div className="family-members-select">
                                    {members
                                        .filter(member => {
                                            // Don't show the currently-selected head in the members list
                                            if (member.id === familyFormData.headId) return false;
                                            if (editingFamily) {
                                                return !isMemberInAnyFamily(member.id) 
                                                    || editingFamily.members?.includes(member.id);
                                            }
                                            return !isMemberInAnyFamily(member.id);
                                        })
                                        .map(member => (
                                            <div key={member.id} className="family-member-checkbox">
                                                <label>
                                                    <input
                                                        type="checkbox"
                                                        checked={familyFormData.members.includes(member.id)}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                // Max total = 8 (head + members)
                                                                if (familyFormData.members.length >= 7) {
                                                                    showToast('Maximum 8 members per family (including the head)', 'error');
                                                                    return;
                                                                }
                                                                setFamilyFormData({
                                                                    ...familyFormData,
                                                                    members: [...familyFormData.members, member.id]
                                                                });
                                                            } else {
                                                                setFamilyFormData({
                                                                    ...familyFormData,
                                                                    members: familyFormData.members.filter(id => id !== member.id)
                                                                });
                                                            }
                                                        }}
                                                    />
                                                    {member.fullName || member.firstName || member.email}
                                                    {member.yearName && <small> ({member.yearName})</small>}
                                                </label>
                                            </div>
                                        ))}
                                </div>
                                <small>
                                    {familyFormData.members.length + (familyFormData.headId ? 1 : 0)}/8 total
                                    {' '}(head {familyFormData.headId ? 'included' : 'not selected'})
                                </small>
                            </div>

                            <div className="family-form-group">
                                <label>Focus Area</label>
                                <input
                                    type="text"
                                    className="family-form-control"
                                    value={familyFormData.focus}
                                    onChange={(e) => setFamilyFormData({ ...familyFormData, focus: e.target.value })}
                                    placeholder="e.g., Outreach, Discipleship"
                                />
                            </div>

                            <div className="family-form-row">
                                <div className="family-form-group">
                                    <label>Start Date</label>
                                    <input
                                        type="date"
                                        className="family-form-control"
                                        value={familyFormData.startDate}
                                        onChange={(e) => setFamilyFormData({ ...familyFormData, startDate: e.target.value })}
                                    />
                                </div>
                                <div className="family-form-group">
                                    <label>End Date (Optional)</label>
                                    <input
                                        type="date"
                                        className="family-form-control"
                                        value={familyFormData.endDate}
                                        onChange={(e) => setFamilyFormData({ ...familyFormData, endDate: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div className="family-form-actions">
                                <button
                                    type="button"
                                    className="family-btn-secondary"
                                    onClick={() => setShowFamilyModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="family-btn-primary"
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <i className="fas fa-spinner fa-spin"></i> Saving...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-save"></i>
                                            {editingFamily ? 'Update' : 'Create'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Family Details Modal */}
            {showMemberModal && selectedFamily && (
                <div className="family-modal" onClick={() => setShowMemberModal(false)}>
                    <div className="family-modal-content family-modal-large" onClick={e => e.stopPropagation()}>
                        <div className="family-modal-header">
                            <h3>{selectedFamily.name} - Members</h3>
                            <button
                                className="family-modal-close"
                                onClick={() => setShowMemberModal(false)}
                            >
                                &times;
                            </button>
                        </div>
                        <div className="family-modal-body">
                            <div className="family-member-details">
                                <div className="family-member-info">
                                    <p><strong>Family Head:</strong> {selectedFamily.headName || 'Not Assigned'}</p>
                                    <p><strong>Focus:</strong> {selectedFamily.focus || 'N/A'}</p>
                                    <p><strong>Total Members:</strong> {getFamilyMemberCount(selectedFamily)}</p>
                                    <p><strong>Start Date:</strong> {formatDate(selectedFamily.startDate) || 'N/A'}</p>
                                    {selectedFamily.endDate && (
                                        <p><strong>End Date:</strong> {formatDate(selectedFamily.endDate)}</p>
                                    )}
                                </div>

                                <div className="family-member-list">
                                    <h4>Family Members ({getFamilyMemberCount(selectedFamily)})</h4>
                                    <div className="family-recent-table">
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th>#</th>
                                                    <th>Name</th>
                                                    <th>Phone</th>
                                                    <th>Email</th>
                                                    <th>Year</th>
                                                    <th>Role</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {getOrderedFamilyMembers(selectedFamily).map((member, index) => {
                                                    const isHead = member.id === selectedFamily.headId;
                                                    return (
                                                        <tr key={member.id}>
                                                            <td>{index + 1}</td>
                                                            <td>
                                                                {member.fullName || member.firstName || 'Unknown'}
                                                                {member.id === currentUser.uid && (
                                                                    <span className="family-you-badge">You</span>
                                                                )}
                                                            </td>
                                                            <td>{member.phone || 'N/A'}</td>
                                                            <td>{member.email || 'N/A'}</td>
                                                            <td>{member.yearName || 'N/A'}</td>
                                                            <td>
                                                                <span className={`family-role-badge ${isHead ? 'head' : 'member'}`}>
                                                                    {isHead ? 'Head' : 'Member'}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="family-modal-footer">
                            <button
                                className="family-btn-secondary"
                                onClick={() => setShowMemberModal(false)}
                            >
                                Close
                            </button>
                            <button
                                className="family-btn-primary"
                                onClick={() => exportFamilyReport(selectedFamily.id)}
                            >
                                <i className="fas fa-file-pdf"></i> Export Members List
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Family;
